import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

import {
  getCurrentSubscription,
  getPermissionState,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/helpers/pushNotifications';

interface UseNotificationsResult {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  requestPermission: () => Promise<{ permission: NotificationPermission; subscribed: boolean }>;
  unsubscribe: () => Promise<boolean>;
}

export function useNotifications(): UseNotificationsResult {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function checkStatus() {
      const supported = await isPushSupported();

      setIsSupported(supported);

      if (supported) {
        setPermission(await getPermissionState());

        const subscription = await getCurrentSubscription();

        setIsSubscribed(!!subscription);
      }

      setIsLoading(false);
    }

    checkStatus();
  }, []);

  const requestPermission = useCallback(async () => {
    setIsLoading(true);

    try {
      const permissionResult = await Notification.requestPermission();

      setPermission(permissionResult);

      const subscribed = permissionResult === 'granted' && (await subscribeToPush());

      setIsSubscribed(subscribed);

      return { permission: permissionResult, subscribed };
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: 'push_notifications', step: 'request_permission' } });

      return { permission: 'denied' as NotificationPermission, subscribed: false };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    const success = await unsubscribeFromPush();

    if (success) setIsSubscribed(false);
    setIsLoading(false);

    return success;
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    requestPermission,
    unsubscribe,
  };
}
